import React, { useState } from "react";
import { ScrollText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import LambdaLogPanel from "./LambdaLogPanel";
import LambdaRunBadge from "./LambdaRunBadge";
import { useLambdaRuns } from "./useLambdaRun";

/**
 * Compact run status for the support pages: a traffic-light badge plus an icon
 * that opens the CloudWatch output for each invocation.
 *
 * Polling lives in the hook here rather than inside the dialog, so the badge
 * keeps resolving while the logs are closed.
 *
 * @param {object} props
 * @param {Array} props.runs invocations from the action's API helper
 * @param {string} [props.title] dialog heading
 * @param {boolean} [props.showBadge] show the traffic-light badge next to the icon
 */
const LambdaRunStatus = ({
  runs = [],
  title = "Execution logs",
  showBadge = true,
}) => {
  const [open, setOpen] = useState(false);
  const {
    runs: trackedRuns,
    status,
    durationMs,
    refresh,
    refreshing,
  } = useLambdaRuns(runs);

  if (runs.length === 0) return null;

  const canShowLogs = trackedRuns.some((run) => run.requestId);

  return (
    <div className="flex items-center gap-1.5">
      {showBadge && <LambdaRunBadge status={status} durationMs={durationMs} />}

      {canShowLogs && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="View execution logs"
          aria-label="View execution logs"
          className="p-1 text-gray-400 hover:text-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-gray-300 transition-colors"
        >
          <ScrollText className="h-4 w-4" aria-hidden />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {trackedRuns.map((run) => (
              <div key={run.requestId || run.functionKey} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    {run.functionKey || run.functionName}
                  </span>
                  <LambdaRunBadge
                    status={run.status}
                    durationMs={run.durationMs}
                  />
                </div>
                <LambdaLogPanel
                  lines={run.lines}
                  status={run.status}
                  finished={run.finished}
                  unavailable={run.unavailable}
                  error={run.error}
                  onRefresh={refresh}
                  refreshing={refreshing}
                  requestId={run.requestId}
                  functionName={run.functionName}
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LambdaRunStatus;
